import { Box, Tab } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { TabContext, TabList, TabPanel } from "@mui/lab";

import {
  deposit,
  increaseLimitLabel,
  limits,
  limitInfoDesc,
  receive,
  sats,
  send,
  swapConvert,
  usdc,
  usdt,
} from "../../messages";
import CommonHeader from "../../CommonHeader";
import Text from "../../text/Text";
import {
  callAPIInterface,
  depositSupportedCountries,
  getCountryObj,
  getSwapLimits,
} from "../../constants";
import LimitList from "./LimitList";
import { useSession } from "../../../hooks/useSession";
import CustomDivider from "../../divider/Divider";
import Button from "../../button/Button";
import UpgradePlanDrawer from "./UpgradePlanDrawer";

const receiveLimit = [
  {
    target_currency: sats,
  },
  { target_currency: usdt },
  { target_currency: usdc },
];

const Limit = () => {
  const { session } = useSession();
  const account = session?.account?.account;
  const userPlan = account?.plan?.plan_name;

  const [isLoading, setIsLoading] = useState({
    send: false,
    swap: false,
    deposit: false,
  });
  const [error, setError] = useState({
    send: false,
    swap: false,
    deposit: false,
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [sendLimit, setSendLimit] = useState(null);
  const [swapLimit, setSwapLimit] = useState();
  const [depositLimit, setDepositLimit] = useState();
  const [openModal, setOpenModal] = useState(false);

  const { currencyCode, isDepositSupported } = useMemo(() => {
    const countryObj = getCountryObj("short_name", account?.country);
    return {
      isDepositSupported: depositSupportedCountries.includes(countryObj?.iso2),
      currencyCode: countryObj?.currency_code,
    };
  }, [session]);

  const fetchSendLimit = useCallback(async () => {
    try {
      setIsLoading((prev) => ({ ...prev, send: true }));
      const res = await callAPIInterface("GET", "/send/limits");
      if (!res) return;
      setSendLimit([res]);
      setError((prev) => ({ ...prev, send: false }));
    } catch (err) {
      setError((prev) => ({ ...prev, send: true }));
    } finally {
      setIsLoading((prev) => ({ ...prev, send: false }));
    }
  }, []);

  const fetchSwapLimit = useCallback(async () => {
    try {
      setIsLoading((prev) => ({ ...prev, swap: true }));
      const limitResponse = await getSwapLimits();
      if (!limitResponse) return;
      setSwapLimit(limitResponse);
      setError((prev) => ({ ...prev, swap: false }));
    } catch (err) {
      setError((prev) => ({ ...prev, swap: true }));
    } finally {
      setIsLoading((prev) => ({ ...prev, swap: false }));
    }
  }, []);

  const fetchDepositLimit = useCallback(async () => {
    try {
      setIsLoading((prev) => ({ ...prev, deposit: true }));
      const res = await callAPIInterface("POST", "/deposit/limits", {
        target_currency: currencyCode,
      });
      if (!res) return;
      setDepositLimit([res]);
      setError((prev) => ({ ...prev, deposit: false }));
    } catch (err) {
      setError((prev) => ({ ...prev, deposit: true }));
    } finally {
      setIsLoading((prev) => ({ ...prev, deposit: false }));
    }
  }, [currencyCode]);

  useEffect(() => {
    fetchSwapLimit();
    fetchSendLimit();
  }, [fetchSwapLimit, fetchSendLimit]);

  useEffect(() => {
    if (isDepositSupported && userPlan) {
      fetchDepositLimit();
    }
  }, [isDepositSupported, userPlan, fetchDepositLimit]);

  const handleTabChange = (e, newValue) => {
    setSelectedTab(newValue);
  };

  const tabData = useMemo(() => {
    const supportedTabs = [
      {
        type: send,
        data: sendLimit,
        isLoading: isLoading.send,
        getData: fetchSendLimit,
        error: error.send,
      },
      {
        type: receive,
        data: receiveLimit,
        isLoading: false,
        getData: null,
        error: false,
      },
      {
        type: swapConvert,
        data: swapLimit,
        isLoading: isLoading.swap,
        getData: fetchSwapLimit,
        error: error.swap,
      },
    ];

    if (isDepositSupported) {
      supportedTabs.push({
        type: deposit,
        data: depositLimit,
        isLoading: isLoading.deposit,
        getData: fetchDepositLimit,
        error: error.deposit,
      });
    }
    return supportedTabs;
  }, [
    sendLimit,
    swapLimit,
    depositLimit,
    isDepositSupported,
    isLoading,
    error,
    fetchSendLimit,
    fetchSwapLimit,
    fetchDepositLimit,
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("tab")) {
      const tabIndex = tabData.findIndex(
        (tab) => tab.type.toLowerCase() === params.get("tab").toLowerCase()
      );
      setSelectedTab(tabIndex === -1 ? 0 : tabIndex);
      params.delete("tab");
      const newSearch = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}${window.location.hash || ""}`
      );
    }
  }, [tabData]);

  const renderTabs = () => {
    return (
      <Box sx={{ width: "100%" }}>
        <TabContext value={selectedTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList variant="scrollable" onChange={handleTabChange}>
              {tabData.map((limit, index) => (
                <Tab
                  key={limit.type}
                  label={limit.type}
                  value={index}
                  sx={{
                    textTransform: "none",
                    fontSize: "16px",
                    fontFamily: "Inter-bold",
                  }}
                />
              ))}
            </TabList>
          </Box>
          <TabPanel className="limits-tab-panel" value={selectedTab}>
            <LimitList
              {...tabData[selectedTab]}
              userPlan={userPlan}
              setOpenModal={setOpenModal}
            />
          </TabPanel>
        </TabContext>
      </Box>
    );
  };

  return (
    <>
      <CommonHeader textAfterIcon={limits} icon="backArrow" showPlan={true} />
      <Box className="options-container">
        <Box className="advance-settings-wrapper limit-wrapper">
          <Box className="module-container">
            <Box className="module-container-content">
              <Text size={16} className="grey-text" sx={{ textAlign: "left" }}>
                {limitInfoDesc(userPlan)}
              </Text>
              {renderTabs()}
            </Box>
            {userPlan !== "Premium" && (
              <Box className="module-container-footer">
                <CustomDivider className="settings-divider" />
                <Button
                  fullWidth
                  variant="contained"
                  className="settings-button"
                  label={increaseLimitLabel}
                  onClick={() => setOpenModal(true)}
                />
              </Box>
            )}
          </Box>
          <UpgradePlanDrawer
            openModal={openModal}
            setOpenModal={setOpenModal}
          />
        </Box>
      </Box>
    </>
  );
};

export default Limit;
